import { api } from '@packages/backend/convex/_generated/api';
import { getUserErrorMessage } from '@packages/shared';
import { createFileRoute, Link, useMatch } from '@tanstack/react-router';
import { useConvex } from 'convex/react';
import type { FunctionReturnType } from 'convex/server';
import { useQuery } from 'convex-helpers/react';
import { differenceInMinutes, format, formatDistanceToNow } from 'date-fns';
import { TriangleAlert } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { ActionDropdown } from '@/components/ActionDropdown';
import { DropdownMenuItemDestructive } from '@/components/DropdownMenuItemDestructive';
import { ErrorAlert } from '@/components/ErrorAlert';
import { HeartbeatVisualization } from '@/components/HeartbeatVisualization';
import { SuperAdminOnly } from '@/components/SuperAdminOnly';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { ColorBadge } from '@/components/ui+/ColorBadge';
import { LoadingIcon } from '@/components/ui+/LoadingIcon';
import { RenameIPadDialog } from '@/routes/restaurant/settings/ipads/components/RenameIPadDialog';
import { UnlinkIPadDialog } from '@/routes/restaurant/settings/ipads/components/UnlinkIPadDialog';
import { BreakingDeployDialog } from './components/BreakingDeployDialog';

export const Route = createFileRoute('/a/ipads/')({
  staticData: {
    breadcrumbs: [{ label: 'iPads' }],
  },
  component: Component,
});

type IPad = FunctionReturnType<typeof api.iPads.adminListDevices>[number];

function Component() {
  const match = useMatch({ strict: false }); // Get the current route match
  const convex = useConvex();
  const { data: iPads, error, isError, isPending } = useQuery(api.iPads.adminListDevices);
  const { data: breakingDeploy } = useQuery(api.platformGlobals.getBreakingDeploy);
  const [iPadToRename, setIPadToRename] = useState<IPad | null>(null);
  const [iPadToUnlink, setIPadToUnlink] = useState<IPad | null>(null);
  const [isBreakingDeployOpen, setIsBreakingDeployOpen] = useState(false);

  // Remote commands + owner actions target a device by its `uuid` and need the owning restaurant.
  const sendCommand = async (
    restaurantId: string,
    posTerminalUuid: string,
    commandType:
      | 'CHECK_FOR_JS_UPDATE'
      | 'RELOAD_POS'
      | 'CLEAR_LOCAL_STORAGE'
      | 'GET_IPAD_TOKEN_HASH'
      | 'GET_ACTIVE_SCREEN',
    successMessage: string,
  ) => {
    try {
      await convex.mutation(api.remoteCommands.issueCommand, {
        restaurantId,
        targetPosTerminalUuid: posTerminalUuid,
        commandType,
      });
      toast.success(successMessage);
    } catch (error) {
      toast.error("Couldn't send command", { description: getUserErrorMessage(error) });
    }
  };

  if (isError) return <ErrorAlert message={error.message} title="Error loading iPads" />;
  if (isPending) return <LoadingIcon />;

  return (
    <>
      {iPadToRename?.restaurantId && (
        <RenameIPadDialog
          iPad={iPadToRename}
          onClose={() => setIPadToRename(null)}
          restaurantId={iPadToRename.restaurantId}
        />
      )}
      {iPadToUnlink?.restaurantId && (
        <UnlinkIPadDialog
          iPad={iPadToUnlink}
          onClose={() => setIPadToUnlink(null)}
          restaurantId={iPadToUnlink.restaurantId}
        />
      )}

      {isBreakingDeployOpen && (
        <BreakingDeployDialog onClose={() => setIsBreakingDeployOpen(false)} />
      )}

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-4">
          <CardTitle>All iPads</CardTitle>
          <SuperAdminOnly>
            <ActionDropdown>
              <DropdownMenuItem onSelect={() => setIsBreakingDeployOpen(true)}>
                Breaking deploy
              </DropdownMenuItem>
            </ActionDropdown>
          </SuperAdminOnly>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Status</TableHead>
                <TableHead>Restaurant</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Printer</TableHead>
                <TableHead>Version</TableHead>
                <TableHead>Last Updated</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {iPads.map((iPad) => {
                const mostRecentHeartbeat: number | null =
                  iPad.heartbeats.length > 0 ? Math.max(...iPad.heartbeats) : null;

                const isOnlineNow =
                  !!mostRecentHeartbeat && differenceInMinutes(Date.now(), mostRecentHeartbeat) < 2;

                const { restaurantId } = iPad;

                // Mirror BreakingDeployGate: an unknown/unparseable version counts as 0. The iPad is
                // still behind the gate while a deploy is armed and its JS build hasn't caught up (a
                // `null` minJsVersion blocks every version).
                const parsedJsVersion = Number(iPad.appJsBuildVersion);
                const deviceJsVersion = Number.isFinite(parsedJsVersion) ? parsedJsVersion : 0;
                const isWaitingForBreakingDeploy =
                  !!breakingDeploy &&
                  (breakingDeploy.minJsVersion === null ||
                    deviceJsVersion < breakingDeploy.minJsVersion);
                const breakingDeployMessage =
                  breakingDeploy?.minJsVersion == null
                    ? 'Breaking change notice shown. Waiting for the update to be applied...'
                    : `Breaking change notice shown. Waiting for v${breakingDeploy.minJsVersion} to be applied...`;

                return (
                  <TableRow key={iPad._id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <HeartbeatVisualization
                          heartbeats={iPad.heartbeats}
                          isOnlineNow={isOnlineNow}
                        />

                        {isOnlineNow && <ColorBadge color="green">ONLINE</ColorBadge>}
                        {!isOnlineNow && !mostRecentHeartbeat && (
                          <ColorBadge color="gray">OFFLINE</ColorBadge>
                        )}
                        {!isOnlineNow && mostRecentHeartbeat && (
                          <Tooltip>
                            <TooltipTrigger>
                              <ColorBadge color="gray">OFFLINE</ColorBadge>
                            </TooltipTrigger>
                            <TooltipContent side="right">
                              {`${formatDistanceToNow(mostRecentHeartbeat, { addSuffix: true })}, ${format(mostRecentHeartbeat, 'h:mm:ss a')}`}
                            </TooltipContent>
                          </Tooltip>
                        )}

                        {isWaitingForBreakingDeploy && (
                          <Tooltip>
                            <TooltipTrigger>
                              <TriangleAlert className="h-4 w-4 text-yellow-500" />
                            </TooltipTrigger>
                            <TooltipContent side="right">{breakingDeployMessage}</TooltipContent>
                          </Tooltip>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{iPad.restaurantName ?? '-'}</TableCell>
                    <TableCell className="font-medium">{iPad.name ?? 'Unnamed iPad'}</TableCell>
                    <TableCell>{iPad.printerName ?? '-'}</TableCell>
                    <TableCell className="font-medium">
                      {iPad.appNativeBuildVersion}/{iPad.appJsBuildVersion}
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">
                        {formatDistanceToNow(iPad.updatedAt, { addSuffix: true })}
                      </div>
                      <div className="text-muted-foreground">
                        {format(iPad.updatedAt, 'h:mm a d-MMM-yy')}
                      </div>
                    </TableCell>
                    <TableCell>
                      <ActionDropdown>
                        {restaurantId && (
                          <SuperAdminOnly>
                            <DropdownMenuSub>
                              <DropdownMenuSubTrigger>Commands</DropdownMenuSubTrigger>
                              <DropdownMenuPortal>
                                <DropdownMenuSubContent>
                                  <DropdownMenuItem
                                    onSelect={() =>
                                      sendCommand(
                                        restaurantId,
                                        iPad.uuid,
                                        'CHECK_FOR_JS_UPDATE',
                                        'JS update check command sent successfully',
                                      )
                                    }
                                  >
                                    Check for JS update
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onSelect={() =>
                                      sendCommand(
                                        restaurantId,
                                        iPad.uuid,
                                        'RELOAD_POS',
                                        'Reload POS command sent successfully',
                                      )
                                    }
                                  >
                                    Reload POS
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onSelect={() =>
                                      sendCommand(
                                        restaurantId,
                                        iPad.uuid,
                                        'CLEAR_LOCAL_STORAGE',
                                        'Clear local storage command sent successfully',
                                      )
                                    }
                                  >
                                    Clear local storage
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onSelect={() =>
                                      sendCommand(
                                        restaurantId,
                                        iPad.uuid,
                                        'GET_IPAD_TOKEN_HASH',
                                        'Get iPad token hash command sent successfully',
                                      )
                                    }
                                  >
                                    Get iPad token hash
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onSelect={() =>
                                      sendCommand(
                                        restaurantId,
                                        iPad.uuid,
                                        'GET_ACTIVE_SCREEN',
                                        'Get active screen command sent successfully',
                                      )
                                    }
                                  >
                                    Get active screen
                                  </DropdownMenuItem>
                                </DropdownMenuSubContent>
                              </DropdownMenuPortal>
                            </DropdownMenuSub>
                          </SuperAdminOnly>
                        )}

                        <DropdownMenuItem asChild>
                          <Link
                            to={'$deviceId'}
                            params={{ deviceId: iPad._id }}
                            from={match.fullPath}
                          >
                            View
                          </Link>
                        </DropdownMenuItem>
                        {restaurantId && (
                          <>
                            <DropdownMenuItem onSelect={() => setIPadToRename(iPad)}>
                              Rename
                            </DropdownMenuItem>
                            <DropdownMenuItemDestructive onSelect={() => setIPadToUnlink(iPad)}>
                              Unlink iPad
                            </DropdownMenuItemDestructive>
                          </>
                        )}
                      </ActionDropdown>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </>
  );
}
